/**
 * Master business-type list used by Onboarding (public registration) and
 * SuperAdmin "Yeni İşletme" form. Single source of truth — adding a new entry
 * here automatically wires it everywhere.
 *
 * UI key → backend persistence model:
 *   backendType  → business.type (existing 7 canonical values, unchanged)
 *   apptCategory → business.appointment_category (optional)
 *   apptSubcat   → business.appointment_subcategory (optional)
 *   iconColor    → swatch color shown in dropdowns
 *
 * Adding a new entry will NOT break older businesses; they keep their existing
 * `type` value and continue to receive labels via BUSINESS_TYPE_LABELS in
 * backend/core/config.py.
 */
import {
  Building2, Dumbbell, Briefcase, Store, Scissors, Sparkles,
  Stethoscope, Brain, Heart,
} from 'lucide-react';

export const MASTER_BUSINESS_TYPES = [
  {
    key: 'erkek_kuafor',
    label: 'Erkek Kuaförü / Berber',
    backendType: 'barber',
    apptCategory: 'kuafor_beauty',
    apptSubcat: 'erkek_kuafor',
    icon: Scissors,
    color: 'bg-blue-600',
    labels: { people: 'Müşteriler', payment: 'Randevu', identifier: 'Müşteri No' },
    terms: {
      customerLabelPlural: 'Müşteriler', customerLabelSingular: 'Müşteri',
      addCustomerLabel: 'Müşteri Ekle', customerNoLabel: 'Müşteri No',
      appointmentLabel: 'Randevular', serviceLabel: 'Hizmet',
    },
    servicePlaceholder: 'Örn: Brazilya fönü',
  },
  {
    key: 'kadin_kuafor',
    label: 'Kadın Kuaförü',
    backendType: 'beauty',
    apptCategory: 'kuafor_beauty',
    apptSubcat: 'kadin_kuafor',
    icon: Scissors,
    color: 'bg-pink-500',
    labels: { people: 'Müşteriler', payment: 'Randevu', identifier: 'Müşteri No' },
    terms: {
      customerLabelPlural: 'Müşteriler', customerLabelSingular: 'Müşteri',
      addCustomerLabel: 'Müşteri Ekle', customerNoLabel: 'Müşteri No',
      appointmentLabel: 'Randevular', serviceLabel: 'Hizmet',
    },
    servicePlaceholder: 'Örn: Brazilya fönü',
  },
  {
    key: 'guzellik_merkezi',
    label: 'Güzellik Merkezi',
    backendType: 'beauty',
    apptCategory: 'kuafor_beauty',
    apptSubcat: 'guzellik_merkezi',
    icon: Sparkles,
    color: 'bg-pink-500',
    labels: { people: 'Müşteriler', payment: 'Randevu', identifier: 'Müşteri No' },
    terms: {
      customerLabelPlural: 'Müşteriler', customerLabelSingular: 'Müşteri',
      addCustomerLabel: 'Müşteri Ekle', customerNoLabel: 'Müşteri No',
      appointmentLabel: 'Randevular', serviceLabel: 'Hizmet',
    },
    servicePlaceholder: 'Örn: Lazer epilasyon',
  },
  {
    key: 'klinik_saglik',
    label: 'Klinik / Sağlık Merkezi',
    backendType: 'dental',
    apptCategory: 'klinik_saglik',
    apptSubcat: 'genel_klinik',
    icon: Stethoscope,
    color: 'bg-cyan-600',
    labels: { people: 'Hastalar', payment: 'Randevu', identifier: 'Hasta No' },
    terms: {
      customerLabelPlural: 'Hastalar', customerLabelSingular: 'Hasta',
      addCustomerLabel: 'Hasta Ekle', customerNoLabel: 'Hasta No',
      appointmentLabel: 'Randevular', serviceLabel: 'Tedavi / Hizmet',
    },
    servicePlaceholder: 'Örn: Diş Beyazlatma',
  },
  {
    key: 'psikolog',
    label: 'Psikolog / Danışmanlık',
    backendType: 'dental',
    apptCategory: 'klinik_saglik',
    apptSubcat: 'psikolog',
    icon: Brain,
    color: 'bg-indigo-500',
    labels: { people: 'Danışanlar', payment: 'Seans', identifier: 'Danışan No' },
    terms: {
      customerLabelPlural: 'Danışanlar', customerLabelSingular: 'Danışan',
      addCustomerLabel: 'Danışan Ekle', customerNoLabel: 'Danışan No',
      appointmentLabel: 'Seanslar', serviceLabel: 'Görüşme',
    },
    servicePlaceholder: 'Örn: Online görüşme',
  },
  {
    key: 'diger_hizmet',
    label: 'Diğer Hizmet İşletmesi',
    backendType: 'general',
    apptCategory: 'diger_hizmet',
    apptSubcat: 'diger',
    icon: Store,
    color: 'bg-emerald-500',
    labels: { people: 'Müşteriler', payment: 'Hizmet Bedeli', identifier: 'Müşteri No' },
    terms: {
      customerLabelPlural: 'Müşteriler', customerLabelSingular: 'Müşteri',
      addCustomerLabel: 'Müşteri Ekle', customerNoLabel: 'Müşteri No',
      appointmentLabel: 'Randevular', serviceLabel: 'Hizmet',
    },
    servicePlaceholder: 'Örn: Hizmet randevusu',
  },
  {
    key: 'apartman',
    label: 'Apartman / Site Yönetimi',
    backendType: 'apartment',
    apptCategory: null, apptSubcat: null,
    icon: Building2,
    color: 'bg-blue-500',
    labels: { people: 'Sakinler', payment: 'Aidat', identifier: 'Daire No' },
    terms: {
      customerLabelPlural: 'Sakinler', customerLabelSingular: 'Sakin',
      addCustomerLabel: 'Sakin Ekle', customerNoLabel: 'Daire / Sakin No',
      appointmentLabel: 'İş Takibi', serviceLabel: 'İşlem',
    },
    servicePlaceholder: '',
  },
  {
    key: 'spor_salonu',
    label: 'Spor Salonu',
    backendType: 'gym',
    apptCategory: 'spor_salonu',
    apptSubcat: 'fitness',
    icon: Dumbbell,
    color: 'bg-orange-500',
    labels: { people: 'Üyeler', payment: 'Üyelik Ücreti', identifier: 'Üye No' },
    terms: {
      customerLabelPlural: 'Üyeler', customerLabelSingular: 'Üye',
      addCustomerLabel: 'Üye Ekle', customerNoLabel: 'Üye No',
      appointmentLabel: 'Randevular', serviceLabel: 'Hizmet',
    },
    servicePlaceholder: 'Örn: Kişisel antrenman',
  },
  {
    key: 'mali_musavir',
    label: 'Mali Müşavir',
    backendType: 'accounting',
    apptCategory: null, apptSubcat: null,
    icon: Briefcase,
    color: 'bg-purple-500',
    labels: { people: 'Mükellefler', payment: 'Hizmet Bedeli', identifier: 'Mükellef No' },
    terms: {
      customerLabelPlural: 'Mükellefler', customerLabelSingular: 'Mükellef',
      addCustomerLabel: 'Mükellef Ekle', customerNoLabel: 'Mükellef No',
      appointmentLabel: 'İş Takibi', serviceLabel: 'Hizmet / İşlem',
    },
    servicePlaceholder: '',
  },
  {
    key: 'diger',
    label: 'Diğer',
    backendType: 'general',
    apptCategory: null, apptSubcat: null,
    icon: Heart,
    color: 'bg-gray-500',
    labels: { people: 'Müşteriler', payment: 'Ödeme', identifier: 'Müşteri No' },
    terms: {
      customerLabelPlural: 'Müşteriler', customerLabelSingular: 'Müşteri',
      addCustomerLabel: 'Müşteri Ekle', customerNoLabel: 'Müşteri No',
      appointmentLabel: 'Randevular', serviceLabel: 'Hizmet',
    },
    servicePlaceholder: 'Örn: Hizmet adı',
  },
];

/** Lookup by UI key. */
export const getMasterTypeByKey = (key) =>
  MASTER_BUSINESS_TYPES.find((t) => t.key === key);

/**
 * Reverse lookup from a saved business (already-persisted) back to a UI key —
 * tries (appointment_category, appointment_subcategory) first, then falls back
 * to backendType so legacy data still has a sensible label.
 */
export const resolveMasterKey = ({ business_type, appointment_category, appointment_subcategory } = {}) => {
  if (appointment_subcategory) {
    const m = MASTER_BUSINESS_TYPES.find(
      (t) => t.apptSubcat === appointment_subcategory && t.apptCategory === appointment_category,
    );
    if (m) return m.key;
  }
  if (appointment_category) {
    const m = MASTER_BUSINESS_TYPES.find((t) => t.apptCategory === appointment_category);
    if (m) return m.key;
  }
  if (business_type) {
    const m = MASTER_BUSINESS_TYPES.find((t) => t.backendType === business_type);
    if (m) return m.key;
  }
  return null;
};

/** Dynamic placeholder for the "Özel Hizmet adı" input, given a subcategory. */
export const placeholderForSubcat = (apptSubcat) => {
  const map = {
    erkek_kuafor: 'Örn: Damat tıraşı',
    kadin_kuafor: 'Örn: Brazilya fönü',
    guzellik_merkezi: 'Örn: Lazer epilasyon',
    genel_klinik: 'Örn: Tetkik değerlendirme',
    dis_klinigi: 'Örn: Diş Beyazlatma',
    psikolog: 'Örn: Online görüşme',
    diyetisyen: 'Örn: Beslenme programı',
    fizyoterapi: 'Örn: Manuel terapi',
    diger: 'Örn: Hizmet randevusu',
  };
  return map[apptSubcat] || 'Örn: Hizmet adı';
};
